/**
 * "Open this RCA job" hand-off to the Agents console (TRUS-684).
 *
 * RCA emails, bell notifications, pushes and the WeaveIn bot's Inbox cards all
 * link to `/agents?job=<id>`. Two ways in:
 *  - a full page load on that URL: the shell opens the Agents tab
 *    (deepLinkTabForPath) and the console reads `job` from the URL, then
 *    removes it (consumeAgentJobFromUrl) so a refresh or Back doesn't re-open it;
 *  - an in-app route (routeStaffPushTarget): the tab switches with no reload
 *    and the job is parked here (requestFocusRcaJob) for the console to take.
 *
 * The request is parked at module scope, not in component state, because the
 * console remounts while auth settles; it is cleared once the console has
 * focused the job (or given up), and expires so a stale request cannot open
 * a job out of the blue later.
 */

export const FOCUS_RCA_JOB_EVENT = 'weavein:agents:focus-job';

/** The paths that open the Agents console (see deepLinkTabForPath). */
const AGENT_PATHS = new Set(['agents', 'platform-agents', 'weavein-agents']);

const PENDING_TTL_MS = 60_000;
let pendingJobId: string | null = null;
let pendingAt = 0;

/** A usable job id (positive integer), or null. */
export function normalizeJobId(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return /^[1-9]\d{0,17}$/.test(s) ? s : null;
}

export function isAgentConsolePath(pathname: string): boolean {
  return AGENT_PATHS.has((pathname || '').replace(/^\/+/, '').replace(/\/+$/, ''));
}

/** Ask the Agents console to open a job (mounted now, or once it mounts). */
export function requestFocusRcaJob(jobId: unknown): void {
  const id = normalizeJobId(jobId);
  if (!id) return;
  pendingJobId = id;
  pendingAt = Date.now();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FOCUS_RCA_JOB_EVENT, { detail: { jobId: id } }));
  }
}

/** The parked request, if still fresh (not cleared: the console clears it when done). */
export function peekPendingRcaJob(): string | null {
  if (pendingJobId && Date.now() - pendingAt <= PENDING_TTL_MS) return pendingJobId;
  pendingJobId = null;
  return null;
}

/** The console has handled this job: forget the request. */
export function clearPendingRcaJob(jobId?: string | null): void {
  if (!jobId || pendingJobId === jobId) pendingJobId = null;
}

/**
 * On an Agents console URL, move `?job=` into the pending request and strip it
 * from the address bar (replaceState: no new history entry), so a refresh or
 * Back doesn't keep re-opening it. Returns the job id taken, if any.
 */
export function consumeAgentJobFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return null;
  }
  if (!isAgentConsolePath(url.pathname) || !url.searchParams.has('job')) return null;
  const id = normalizeJobId(url.searchParams.get('job'));
  url.searchParams.delete('job');
  try {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* a sandboxed frame may refuse; the request below still works */
  }
  if (id) {
    pendingJobId = id;
    pendingAt = Date.now();
  }
  return id;
}
